import React, { useMemo, useState } from 'react';
import CardActiveTasks from '../card-active-tasks/index.web';
import CardNextEvent from '../card-next-event/index.web';
import useLogic from './logic.web';
import { VStack, Button, Icon, Box, Flex, useDisclosure, } from '@chakra-ui/react';
import FONTS from '../../../../../../../constants/fonts';
import COLORS from '../../../../../../../constants/colors';
import { tKeys } from '../../../../../../../translations/keys';
import { useTranslation } from 'react-i18next';
import CustomTaskDetail from '../../../../../../task-details/custom-task-detail/index.web';
import VoteResults from '../../../../../../vote-results/index.web';
import LoadingWrapper from '../../../../../../../components/utility/loading-wrapper/index.web';
import { FaUsers, FaCalendarAlt, FaFileAlt, FaUserPlus, FaCheck, } from 'react-icons/fa';
import Text from '../../../../../../../components/content/text';
import { useGroupListLogic } from '../../../../../more/components/list/group-list/logic.web';
import { EditGroup } from '../../../../../more/components/modal/edit-group/index.web';
import { useCompaniesContext } from '../../../../../../../contexts/companies/index.web';
import { useReadGroups } from '../../../../../../../services/online/groups/groups';
import CreateEvent from '../../../../../calendar/create-event/index.web';
import { useReadEvents, useReadEventTypes, } from '../../../../../../../services/online/calendar/calendar';
import { useReadDocuments, } from '../../../../../../../services/online/repository/repository';
import CreateDocument from '../../../../../more/repository/create-document/index.web';
import { useReadEnrolments } from '../../../../../../../services/online/enrolments/enrolments';
import InviteMember from '../../../../../../invite-member/index.web';
import { useFilePicker } from 'use-file-picker';
import { onSelectedFileSuccess } from '../../../../../../../utils/selectFile.web';
const CardUserTasksAndNextEvent = ({ closestEvents, isLoadingEvent, }) => {
    // Attributes
    const { t } = useTranslation();
    const { selectedCompanyId } = useCompaniesContext();
    const { isLoading, onTaskPress, userTasks, handleOnPressEvent, currentUser, isOpenTaskDetails, isOpenVoteResult, onCloseTaskDetails, onCloseVoteResult, selectedTask, voteResultParams, isAdmin, } = useLogic();
    const { handleCreateGroupClick, selectedGroup, upsertGroupDisclosure } = useGroupListLogic();
    const createEventDisclosure = useDisclosure();
    const createDocumentDisclosure = useDisclosure();
    const inviteMemberDisclosure = useDisclosure();
    // State for selected file from picker
    const [selectedFile, setSelectedFile] = useState();
    // File Picker Hook
    const { openFilePicker } = useFilePicker({
        readAs: 'DataURL',
        accept: [
            'image/jpeg',
            'image/png',
            'image/gif',
            '.txt',
            '.pdf',
            '.doc',
            '.docx',
            '.ppt',
            '.pptx',
            '.xls',
            '.xlsx',
            '.csv',
        ],
        onFilesSelected: async (chosenDocuments) => await onSelectedFileSuccess(chosenDocuments, setSelectedFile, createDocumentDisclosure.onOpen),
    });
    // Fetch groups for the company
    const { data: groups, refetch: refetchGroups, isLoading: isLoadingGroups, } = useReadGroups({
        'company-id': selectedCompanyId,
    }, {
        query: {
            enabled: !!selectedCompanyId,
        },
    });
    // Fetch all event types first
    const { data: allEventsTypes, isLoading: isLoadingEventTypes } = useReadEventTypes({
        'company-id': selectedCompanyId,
    }, { query: { enabled: !!selectedCompanyId } });
    // Fetch events for the company using all fetched event types
    const { data: events, refetch: refetchEvents, isLoading: isLoadingEvents, } = useReadEvents({
        'company-id': selectedCompanyId,
        'is-invited': false,
        'event-types': allEventsTypes?.map(e => e.id),
    }, {
        query: {
            enabled: !!selectedCompanyId && !!allEventsTypes && !isLoadingEventTypes,
        },
    });
    // Fetch documents for the company
    const { data: documentsData, refetch: refetchDocuments, isLoading: isLoadingDocuments, } = useReadDocuments({
        'company-id': selectedCompanyId,
        'document-types': allEventsTypes?.map(e => e.id) ?? [],
    }, {
        query: {
            enabled: !!selectedCompanyId,
        },
    });
    // Fetch active enrolments (members) for the company
    const { data: enrolmentsData, refetch: refetchEnrolments, isLoading: isLoadingEnrolments, } = useReadEnrolments({
        'company-id': selectedCompanyId,
        'show-active': true,
        'page-size': 2,
    }, {
        query: {
            enabled: !!selectedCompanyId,
        },
    });
    // Flatten the categorized document list for a simple count
    const documents = useMemo(() => documentsData?.flatMap(category => category.items) ?? [], [documentsData]);
    const hasCreatedGroup = groups && groups.length > 0;
    const hasCreatedEvent = events && events.length > 0;
    const hasAddedDocuments = documents && documents.length > 0;
    const hasInvitedMembers = enrolmentsData && enrolmentsData.items && enrolmentsData.items.length > 1;
    const isEmpty = !events?.length && !userTasks?.length;
    // Render
    return (<VStack width={'full'} alignItems={'flex-start'} spacing={'2rem'}>
      {/* Title */}

      <LoadingWrapper isLoading={(isLoadingEvent && isLoading) ||
            isLoadingGroups ||
            isLoadingEventTypes ||
            isLoadingEvents ||
            isLoadingDocuments ||
            isLoadingEnrolments}>
        {isAdmin && isEmpty ? (<VStack width={'full'} spacing={'2rem'} alignItems={'flex-start'}>
            <Box width={'full'} p={'1.5rem'} borderRadius={'.5rem'} bg={COLORS.White.T500.value} borderWidth={1} borderColor={COLORS.Line.value} boxShadow={'sm'}>
              <VStack width={'full'} spacing={'1.5rem'} alignItems={'flex-start'}>
                <VStack width={'full'} alignItems={'flex-start'} spacing={'0.5rem'}>
                  <Text font={FONTS.T1.T16Px.Bold700.value} color={COLORS.Govrn.Blue.T500.value} numberOfLines={5}>
                    {t('homepage.emptyState.title', {
                value: currentUser?.firstname,
            })}
                  </Text>
                  <Text font={FONTS.T1.T14Px.Regular400.value} color={COLORS.Text.T400.value} numberOfLines={5}>
                    {t('homepage.emptyState.description')}
                  </Text>
                </VStack>

                {/* Workflow Steps */}
                <VStack width={'full'} spacing={'1.5rem'} alignItems={'flex-start'}>
                  {/* Step 1 */}
                  <Flex width={'full'} alignItems={'center'} gap={'1rem'}>
                    <Box borderRadius={'full'} bg={hasCreatedGroup
                ? COLORS.Success.T500.value
                : COLORS.Govrn.Blue.T500.value} color={COLORS.White.T500.value} width={'24px'} height={'24px'} display={'flex'} alignItems={'center'} justifyContent={'center'} fontSize={'12px'} fontWeight={'bold'}>
                      {hasCreatedGroup ? <Icon as={FaCheck}/> : '1'}
                    </Box>
                    <Button leftIcon={<Icon as={FaUsers}/>} colorScheme="blue" variant="solid" onClick={handleCreateGroupClick} isDisabled={hasCreatedGroup || isLoadingGroups}>
                      {t('homepage.emptyState.createGroup')}
                    </Button>
                  </Flex>
                  <Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T400.value} style={{
                paddingLeft: 'calc(24px + 1rem)',
                marginTop: '-0.5rem',
            }}>
                    {t('homepage.emptyState.createGroupDescription')}
                  </Text>

                  {/* Step 2 */}
                  <Flex width={'full'} alignItems={'center'} gap={'1rem'}>
                    <Box borderRadius={'full'} bg={hasCreatedEvent
                ? COLORS.Success.T500.value
                : COLORS.Govrn.PastelGreen.T500.value} color={COLORS.White.T500.value} width={'24px'} height={'24px'} display={'flex'} alignItems={'center'} justifyContent={'center'} fontSize={'12px'} fontWeight={'bold'}>
                      {hasCreatedEvent ? <Icon as={FaCheck}/> : '2'}
                    </Box>
                    <Button leftIcon={<Icon as={FaCalendarAlt}/>} colorScheme="green" variant="solid" onClick={createEventDisclosure.onOpen} isDisabled={hasCreatedEvent || isLoadingEvents}>
                      {t('homepage.emptyState.createEvent')}
                    </Button>
                  </Flex>
                  <Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T400.value} style={{
                paddingLeft: 'calc(24px + 1rem)',
                marginTop: '-0.5rem',
            }}>
                    {t('homepage.emptyState.createEventDescription')}
                  </Text>

                  {/* Step 3 */}
                  <Flex width={'full'} alignItems={'center'} gap={'1rem'}>
                    <Box borderRadius={'full'} bg={hasAddedDocuments
                ? COLORS.Success.T500.value
                : COLORS.Govrn.Purple.T500.value} color={COLORS.White.T500.value} width={'24px'} height={'24px'} display={'flex'} alignItems={'center'} justifyContent={'center'} fontSize={'12px'} fontWeight={'bold'}>
                      {hasAddedDocuments ? <Icon as={FaCheck}/> : '3'}
                    </Box>
                    <Button leftIcon={<Icon as={FaFileAlt}/>} colorScheme="purple" variant="solid" onClick={openFilePicker} isDisabled={hasAddedDocuments || isLoadingDocuments}>
                      {t('homepage.emptyState.addDocuments')}
                    </Button>
                  </Flex>
                  <Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T400.value} style={{
                paddingLeft: 'calc(24px + 1rem)',
                marginTop: '-0.5rem',
            }}>
                    {t('homepage.emptyState.addDocumentsDescription')}
                  </Text>

                  {/* Optional Step: Invite Members */}
                  <Flex width={'full'} alignItems={'center'} gap={'1rem'}>
                    <Box borderRadius={'full'} bg={hasInvitedMembers
                ? COLORS.Success.T500.value
                : COLORS.Govrn.Grey.T200.value} color={hasInvitedMembers
                ? COLORS.White.T500.value
                : COLORS.Govrn.Grey.T600.value} width={'24px'} height={'24px'} display={'flex'} alignItems={'center'} justifyContent={'center'} fontSize={'12px'} fontWeight={'bold'}>
                      {hasInvitedMembers ? (<Icon as={FaCheck}/>) : (<Icon as={FaUserPlus}/>)}
                    </Box>
                    <Button leftIcon={<Icon as={FaUserPlus}/>} colorScheme="orange" variant="solid" onClick={inviteMemberDisclosure.onOpen} isDisabled={hasInvitedMembers || isLoadingEnrolments}>
                      {t('homepage.emptyState.inviteMembers')}
                    </Button>
                  </Flex>
                  <Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T400.value} style={{
                paddingLeft: 'calc(24px + 1rem)',
                marginTop: '-0.5rem',
            }}>
                    {t('homepage.emptyState.inviteMembersDescription')}
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </VStack>) : (<VStack width={'full'} alignItems={'flex-start'} spacing={'2rem'}>
            <VStack width={'full'} alignItems={'flex-start'}>
              <Text font={FONTS.T1.T16Px.Bold700.value}>
                {t(tKeys.homepage.hello, { value: currentUser?.firstname })}
              </Text>
              <Text font={FONTS.T1.T14Px.Regular400.value}>
                {t(tKeys.homepage.description)}
              </Text>
            </VStack>
            <CardNextEvent events={closestEvents} handleOnPressEvent={handleOnPressEvent}/>

            {/*  Active Tasks */}
            {userTasks?.length > 0 && (
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            <CardActiveTasks onTaskPress={onTaskPress} tasks={userTasks}/>)}
          </VStack>)}
      </LoadingWrapper>
      {/* Modals */}
      <CustomTaskDetail task={selectedTask} isOpen={isOpenTaskDetails} onClose={onCloseTaskDetails}/>
      <VoteResults isOpen={isOpenVoteResult} onClose={onCloseVoteResult} voteResultParams={voteResultParams}/>
      {/* Add Group Modal */}
      <EditGroup isOpen={upsertGroupDisclosure.isOpen} onClose={upsertGroupDisclosure.onClose} refetchGroups={refetchGroups} group={selectedGroup}/>
      {/* Create Event Modal */}
      <CreateEvent isOpen={createEventDisclosure.isOpen} onClose={createEventDisclosure.onClose} additionalFunctionOnCreate={refetchEvents}/>
      {/* Render CreateDocument Modal instead of DocumentModal */}
      <CreateDocument isOpen={createDocumentDisclosure.isOpen} onClose={createDocumentDisclosure.onClose} selectedFile={selectedFile} refetchDocuments={refetchDocuments}/>
      {/* Invite Member Modal */}
      <InviteMember isOpen={inviteMemberDisclosure.isOpen} onClose={inviteMemberDisclosure.onClose} refetchAfterInvite={refetchEnrolments} redirectionAfterInvite={() => {
            console.log('Redirect after invite placeholder');
        }}/>
    </VStack>);
};
export default CardUserTasksAndNextEvent;
